import { EnumProductType } from "gql/generated";
import {
  Box3dCenter,
  Calendar,
  PageFlip,
  Refresh,
  SeaAndSun,
} from "iconoir-react";
import {
  ForwardRefExoticComponent,
  RefAttributes,
  SVGProps,
} from "react";

export type ProductTypeIcon = ForwardRefExoticComponent<
  Omit<SVGProps<SVGSVGElement>, "ref"> & RefAttributes<SVGSVGElement>
>;

export type ProductTypeConfig = {
  i18nKey: "ACCOMMODATION" | "EVENT" | "FEE" | "RENEWABLE_FEE" | "MERCH";
  orderTerm: "Reservation" | "Order";
  orderTermLowercase: "reservation" | "order";
  widgetTerm: "Checkout";
  hasDates: boolean;
  hasCheckInOut: boolean;
  hasGuestCount: boolean;
  hasQuantity: boolean;
  reservationDetailsIcon: ProductTypeIcon;
  reservationDetailsCardVariant: "accommodation" | "quantity";
};

const defaultProductTypeConfig: ProductTypeConfig = {
  i18nKey: "FEE",
  orderTerm: "Order",
  orderTermLowercase: "order",
  widgetTerm: "Checkout",
  hasDates: false,
  hasCheckInOut: false,
  hasGuestCount: false,
  hasQuantity: true,
  reservationDetailsIcon: PageFlip,
  reservationDetailsCardVariant: "quantity",
};

export const productTypeConfig: Record<EnumProductType, ProductTypeConfig> = {
  [EnumProductType.Accommodation]: {
    i18nKey: "ACCOMMODATION",
    orderTerm: "Reservation",
    orderTermLowercase: "reservation",
    widgetTerm: "Checkout",
    hasDates: true,
    hasCheckInOut: true,
    hasGuestCount: true,
    hasQuantity: false,
    reservationDetailsIcon: SeaAndSun,
    reservationDetailsCardVariant: "accommodation",
  },
  [EnumProductType.Event]: {
    i18nKey: "EVENT",
    orderTerm: "Order",
    orderTermLowercase: "order",
    widgetTerm: "Checkout",
    hasDates: true,
    hasCheckInOut: false,
    hasGuestCount: false,
    hasQuantity: true,
    reservationDetailsIcon: Calendar,
    reservationDetailsCardVariant: "quantity",
  },
  [EnumProductType.Fee]: defaultProductTypeConfig,
  [EnumProductType.RenewableFee]: {
    i18nKey: "RENEWABLE_FEE",
    orderTerm: "Order",
    orderTermLowercase: "order",
    widgetTerm: "Checkout",
    hasDates: false,
    hasCheckInOut: false,
    hasGuestCount: false,
    hasQuantity: true,
    reservationDetailsIcon: Refresh,
    reservationDetailsCardVariant: "quantity",
  },
  [EnumProductType.Merch]: {
    i18nKey: "MERCH",
    orderTerm: "Order",
    orderTermLowercase: "order",
    widgetTerm: "Checkout",
    hasDates: false,
    hasCheckInOut: false,
    hasGuestCount: false,
    hasQuantity: true,
    reservationDetailsIcon: Box3dCenter,
    reservationDetailsCardVariant: "quantity",
  },
};

export const getProductTypeConfig = (
  productType?: EnumProductType | null,
): ProductTypeConfig =>
  productType ? productTypeConfig[productType] ?? defaultProductTypeConfig : defaultProductTypeConfig;
